import clsx from 'clsx';
import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { TrackingLocation } from '@tracking';

import { Article } from '../../interfaces/homepage';
import FeaturedArticle from './FeaturedArticle';
import SideArticle from './SideArticle';

export enum Page {
  Highlight = 'highlight',
  Home = 'home',
}

interface TopArticlesSectionProps {
  page: Page;
  sideArticles: Article[];
  topArticle: Article;
}

const TopArticlesSection = ({
  page,
  sideArticles,
  topArticle,
}: TopArticlesSectionProps): JSX.Element => {
  const { Track } = useEventTracking({ location: TrackingLocation.TopArticles });
  return (
    <Track>
      <div
        className={clsx(
          'mx-0 flex flex-col flex-nowrap gap-32 gap-x-0 border-0 border-b border-solid border-grayscale-gray pb-48',
          {
            'md:flex-row md:gap-48 lg:gap-x-112': page === Page.Highlight,
            'sm:flex-row sm:gap-28 lg:gap-56': page === Page.Home,
          }
        )}
      >
        {topArticle && (
          <FeaturedArticle
            className={clsx({
              'sm:pb-48 md:border-b-0 md:pb-0': page === Page.Highlight,
              'sm:border-b-0 sm:pb-0': page === Page.Home,
            })}
            featuredArticle={topArticle}
            isFluid={true}
          />
        )}
        <div
          className={clsx('w-full shrink-0', {
            'md:w-[258px]': page === Page.Highlight,
            'sm:w-[213px] lg:w-[256px]': page === Page.Home,
          })}
        >
          {sideArticles?.length > 0 &&
            sideArticles.map((article, index) => (
              <SideArticle
                articleIndex={index}
                key={`${article.id}-${index}`}
                article={article}
                imageClassName={clsx('!min-w-0 md:w-full', {
                  'aspect-[16/9] leading-[0] sm:w-[302px] sm:h-[calc(302px*9/16)] md:w-full md:h-auto':
                    page === Page.Highlight,
                  'sm:w-[213px] lg:w-[256px] sm:h-[calc(213px*9/16)] lg:h-[calc(256px*9/16)]':
                    page === Page.Home,
                })}
                page={page}
              />
            ))}
        </div>
      </div>
    </Track>
  );
};

export default TopArticlesSection;
